import { useMemo, useState } from "react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, LayoutGrid, LayoutList, GanttChart, Waypoints, Plus } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { ProjectEmptyState, ProjectToolbar, ProjectStatusTabs } from "@/components/ProjectLayout";
import { TaskCreateDialog, type TaskDefaults } from "@/components/TaskCreateDialog";
import { HealthDot, ProgressRing, TaskLeadPicker, TaskPriorityPicker, TaskStatusIcon, TaskStatusPicker, TaskDateButton } from "@/components/TaskProps";
import { Timeline, type Zoom } from "@/components/Timeline";
import { TreeDropRow, TreeDropRoot, useTreeDrag } from "@/components/TreeDrag";
import { TaskGraph } from "@/components/TaskGraph";
import { Avatar } from "@/components/PropertyIcons";
import { errorMessage } from "@/lib/api";
import { liveTasks, patchTask, useProject, type ProjectData, type Task } from "@/lib/store";
import { TASK_STATUSES, TASK_STATUS_COLOR, flattenTree, isBlocked, taskProgress, taskTree, type TaskStatus } from "@/lib/tasks";
import { matchesTaskTab } from "@/lib/workflow";
import { toast } from "@/lib/toast";
import { useViews } from "@/lib/views";
import { ProjectCrumb } from "./ProjectIssues";
import { ProjectLoading, useLoadProject } from "./ProjectHome";

const route = getRouteApi("/$ws/project/$key/tasks/$tab");
const TABS = ["all", ...TASK_STATUSES] as const;
type Tab = (typeof TABS)[number];
type Layout = "list" | "board" | "timeline" | "graph";

export function ProjectTasksPage() {
  const { ws, key, tab: raw } = route.useParams();
  const tab: Tab = TABS.includes(raw as Tab) ? (raw as Tab) : "all";
  return <TasksView key={`${ws}/${key}/${tab}`} ws={ws} kkey={key} tab={tab} />;
}

function TasksView({ ws, kkey: key, tab }: { ws: string; kkey: string; tab: Tab }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useLoadProject(ws, key);
  const d = useProject(ws, key);
  const viewKey = `project:${key}:tasks`;
  const saved = useViews((s) => s.views[viewKey]);
  const setView = useViews((s) => s.set);
  const layout = ((saved as unknown as { layout?: string } | undefined)?.layout ?? "list") as Layout;
  const zoom = ((saved as unknown as { zoom?: Zoom } | undefined)?.zoom ?? "month") as Zoom;
  const [createOpen, setCreateOpen] = useState(false);
  const [defaults, setDefaults] = useState<TaskDefaults | undefined>();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const tasks = useMemo(() => {
    if (!d) return [];
    return liveTasks(d).filter((task) => matchesTaskTab(task.status, tab));
  }, [d, tab]);

  if (!d) return <ProjectLoading ws={ws} kkey={key} />;
  const canEdit = !!d.project.role;
  const open = (def?: TaskDefaults) => { setDefaults({ ...(tab !== "all" ? { status: tab } : {}), ...def }); setCreateOpen(true); };
  const layouts: { id: Layout; icon: React.ReactNode }[] = [
    { id: "list", icon: <LayoutList className="h-3.5 w-3.5" /> },
    { id: "board", icon: <LayoutGrid className="h-3.5 w-3.5" /> },
    { id: "timeline", icon: <GanttChart className="h-3.5 w-3.5" /> },
    { id: "graph", icon: <Waypoints className="h-3.5 w-3.5" /> },
  ];

  return (
    <>
      <PageHeader
        crumbs={[<ProjectCrumb key="p" ws={ws} d={d} />, t("nav.tasks")]}
        right={
          <>
            {canEdit && (
              <button className="btn btn-primary h-8 text-[13px]" onClick={() => open()}>
                <Plus className="h-3.5 w-3.5" /> {t("task.newTask")}
              </button>
            )}
          </>
        }
      />
      <ProjectToolbar right={
            <div className="flex gap-0.5 rounded-md bg-sidebar p-0.5">
              {layouts.map((l) => (
                <button key={l.id} className={`flex items-center gap-1 h-7 rounded px-2 text-[12px] ${layout === l.id ? "bg-elevated text-fg shadow" : "text-muted hover:text-fg"}`} title={t(`taskLayout.${l.id}`)} onClick={() => setView(viewKey, { layout: l.id } as never)}>
                  {l.icon} <span className="hidden lg:inline">{t(`taskLayout.${l.id}`)}</span>
                </button>
              ))}
            </div>
      }>
        <ProjectStatusTabs ws={ws} projectKey={key} kind="tasks" selected={tab} label={t("issue.status")} tabs={TABS.map((id) => ({ id, label: t(`taskTabs.${id}`) }))} />
      </ProjectToolbar>

      {tasks.length === 0 ? (
        <ProjectEmptyState title={tab === "all" ? t("task.emptyTitle") : t(`taskTabs.${tab}`)} body={liveTasks(d).length === 0 ? t("task.emptyBody") : t("task.emptyStatusBody")} actions={canEdit ? <button className="btn btn-primary" onClick={() => open()}>{t("task.newTask")}</button> : undefined} />
      ) : layout === "board" ? (
        <Board d={d} tasks={tasks} status={tab === "all" ? undefined : tab} onCreate={canEdit ? (status) => open({ status }) : undefined} />
      ) : layout === "timeline" ? (
        <TasksTimeline d={d} tasks={tasks} zoom={zoom} onZoom={(z) => setView(viewKey, { zoom: z } as never)} />
      ) : layout === "graph" ? (
        <TaskGraph d={d} tasks={tasks} />
      ) : (
        <TaskListTree d={d} tasks={tasks} collapsed={collapsed} onExpand={(id) => setCollapsed((s) => { if (!s.has(id)) return s; const n = new Set(s); n.delete(id); return n; })} onToggle={(id) => setCollapsed((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; })} onAddSub={canEdit ? (parent) => open({ parent_id: parent }) : undefined} onOpen={(id) => void navigate({ to: "/$ws/task/$id", params: { ws, id } })} />
      )}
      <TaskCreateDialog d={d} open={createOpen} onOpenChange={setCreateOpen} defaults={defaults} />
    </>
  );
}

function TaskListTree({ d, tasks, collapsed, onToggle, onExpand, onAddSub, onOpen }: { d: ProjectData; tasks: Task[]; collapsed: Set<string>; onToggle: (id: string) => void; onExpand: (id: string) => void; onAddSub?: (parent: string) => void; onOpen: (id: string) => void }) {
  const { t, i18n } = useTranslation();
  const rows = flattenTree(taskTree(d, tasks), collapsed);
  const drag = useTreeDrag({ items: liveTasks(d), disabled: !d.project.role, onExpand,
    onMove: (id, move) => patchTask(d.tasks[id]!, move),
  });
  return (
    <div data-tree-scroll className="flex-1 overflow-y-auto">
      <div className="flex h-8 items-center gap-2 border-b border-border px-3 text-[11px] font-medium uppercase tracking-wide text-faint">
        <span className="flex-1">{t("task.name")}</span>
        <span className="w-[90px]">{t("task.health")}</span>
        <span className="w-[60px]">{t("issue.priority")}</span>
        <span className="w-[40px]">{t("task.lead")}</span>
        <span className="w-[110px]">{t("task.targetDate")}</span>
        <span className="w-[90px] text-right">{t("task.progress")}</span>
      </div>
      {rows.map(({ task, depth, children }) => {
        const prog = taskProgress(d, task);
        const blocked = isBlocked(d, task.id);
        return (
          <TreeDropRow key={task.id} id={task.id} tree={drag} depth={depth}><div className="group flex h-[38px] items-center gap-2 border-b border-border/60 px-3 text-[13px] hover:bg-hover/60" style={{ paddingLeft: 12 + depth * 20 }}>
            {drag.handle(task, task.name)}
            <button aria-label={collapsed.has(task.id) ? t("tree.expand") : t("tree.collapse")} className={`w-4 text-faint ${children.length ? "" : "invisible"}`} onClick={() => onToggle(task.id)}>
              {collapsed.has(task.id) ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            <TaskStatusPicker task={task} compact />
            <button className="min-w-0 flex-1 truncate text-left hover:underline" onClick={() => onOpen(task.id)}>
              {task.name}
              {children.length > 0 && <span className="ml-1.5 text-[11.5px] text-faint">{children.length} {t("task.subTasksShort")}</span>}
              {blocked && <span className="ml-1.5 rounded bg-warning/20 px-1 text-[11px] text-warning">{t("task.blocked")}</span>}
              {task.milestone_id && d.milestones[task.milestone_id] && <span className="ml-1.5 text-[11.5px] text-faint">⚑ {d.milestones[task.milestone_id]!.name}</span>}
            </button>
            {onAddSub && (
              <button className="rounded p-1 text-faint opacity-0 hover:bg-active hover:text-fg group-hover:opacity-100" title={t("task.addSubTask")} onClick={() => onAddSub(task.id)}><Plus className="h-3.5 w-3.5" /></button>
            )}
            <span className="w-[90px]"><HealthDot health={d.health[task.id]?.health} /></span>
            <span className="w-[60px]"><TaskPriorityPicker task={task} compact /></span>
            <span className="w-[40px]"><TaskLeadPicker d={d} task={task} compact /></span>
            <span className="w-[110px] text-[12px] text-muted">{task.target_date ? new Date(task.target_date + "T00:00:00").toLocaleDateString(i18n.language, { month: "short", day: "numeric" }) : <TaskDateButton task={task} field="target_date" compact />}</span>
            <span className="flex w-[90px] items-center justify-end gap-1.5 text-[12px] text-muted">
              <ProgressRing ratio={prog?.ratio ?? null} />
              {prog ? `${Math.round(prog.ratio * 100)}%` : "—"}
            </span>
          </div></TreeDropRow>
        );
      })}
      <TreeDropRoot tree={drag} />
    </div>
  );
}

function Board({ d, tasks, status: selectedStatus, onCreate }: { d: ProjectData; tasks: Task[]; status?: TaskStatus; onCreate?: (status: string) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [over, setOver] = useState<string | null>(null);
  const drop = async (e: React.DragEvent, status: TaskStatus) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData("text/task-id");
    const task = d.tasks[id];
    if (!task || task.status === status) return;
    try {
      await patchTask(task, { status });
    } catch (err) {
      toast(errorMessage(err), { tone: "error" });
    }
  };
  return (
    <div className="flex flex-1 gap-3 overflow-x-auto p-3">
      {(selectedStatus ? [selectedStatus] : [...TASK_STATUSES, ...(tasks.some((task) => task.status === "canceled") ? ["canceled" as const] : [])]).map((status) => {
        const list = tasks.filter((x) => x.status === status).sort((a, b) => (a.sort_order < b.sort_order ? -1 : 1));
        return (
          <div key={status} className={`flex w-[270px] shrink-0 flex-col rounded-lg border ${over === status ? "border-accent bg-accent/5" : "border-border bg-sidebar/50"}`} onDragOver={(e) => { e.preventDefault(); setOver(status); }} onDragLeave={() => setOver((o) => (o === status ? null : o))} onDrop={(e) => void drop(e, status)}>
            <header className="group/h flex h-9 items-center gap-2 px-3 text-[13px]">
              <TaskStatusIcon status={status} />
              <span className="font-medium">{t(`taskStatus.${status}`)}</span>
              <span className="text-muted">{list.length}</span>
              {onCreate && <button className="ml-auto rounded p-1 text-muted opacity-0 hover:bg-hover group-hover/h:opacity-100" onClick={() => onCreate(status)}><Plus className="h-3.5 w-3.5" /></button>}
            </header>
            <div className="flex flex-1 flex-col gap-1.5 overflow-y-auto px-2 pb-2">
              {list.map((task) => {
                const prog = taskProgress(d, task);
                return (
                  <div key={task.id} draggable onDragStart={(e) => { e.dataTransfer.setData("text/task-id", task.id); }} className="cursor-grab rounded-md border border-border bg-elevated p-2.5 hover:border-border-strong" style={{ borderLeft: `3px solid ${task.color ?? TASK_STATUS_COLOR[status]}` }}>
                    <button className="block w-full text-left text-[13px] font-medium hover:underline" onClick={() => void navigate({ to: "/$ws/task/$id", params: { ws: d.slug, id: task.id } })}>{task.name}</button>
                    {task.summary && <div className="mt-0.5 truncate text-[12px] text-muted">{task.summary}</div>}
                    <div className="mt-2 flex items-center gap-2 text-[11.5px] text-muted">
                      <ProgressRing ratio={prog?.ratio ?? null} size={14} /> {prog ? `${Math.round(prog.ratio * 100)}%` : "—"}
                      <HealthDot health={d.health[task.id]?.health} />
                      {isBlocked(d, task.id) && <span className="rounded bg-warning/20 px-1 text-warning">{t("task.blocked")}</span>}
                      <span className="ml-auto"><Avatar user={task.lead_id ? d.users[task.lead_id] : undefined} size={16} /></span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TasksTimeline({ d, tasks, zoom, onZoom }: { d: ProjectData; tasks: Task[]; zoom: Zoom; onZoom: (z: Zoom) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rows = flattenTree(taskTree(d, tasks), new Set()).map(({ task, depth }) => {
    const prog = taskProgress(d, task);
    return {
      id: task.id,
      depth,
      label: (
        <>
          <TaskStatusIcon status={task.status} />
          <span className="truncate">{task.name}</span>
          {isBlocked(d, task.id) && <span className="text-warning" title={t("task.blocked")}>⚠</span>}
        </>
      ),
      start: task.start_date,
      end: task.target_date,
      color: task.color ?? TASK_STATUS_COLOR[task.status as TaskStatus] ?? "var(--accent)",
      progress: prog?.ratio ?? null,
      muted: task.status === "completed" || task.status === "canceled",
      onClick: () => void navigate({ to: "/$ws/task/$id", params: { ws: d.slug, id: task.id } }),
      onChange: d.project.role ? (s: string | null, e: string | null) => void patchTask(task, { start_date: s, target_date: e }).catch((err) => toast(errorMessage(err), { tone: "error" })) : undefined,
    };
  });
  const ids = new Set(tasks.map((x) => x.id));
  const edges = Object.values(d.dependencies).filter((x) => ids.has(x.predecessor_id) && ids.has(x.successor_id)).map((x) => ({ from: x.predecessor_id, to: x.successor_id }));
  return <Timeline rows={rows} edges={edges} zoom={zoom} onZoom={onZoom} />;
}
